import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Stars as StarsIcon,
  CheckCircle as RequirementIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

export default function SurveyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [me, setMe] = useState(null); // current user with dynamicProfile
  const [profileQuestionTitles, setProfileQuestionTitles] = useState({}); // questionId -> title
  const [externalLoading, setExternalLoading] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const translateCategory = (key) => {
    if (!key) return '';
    const map = {
      'online-surveys': 'Онлайн-опросы',
      'online-interviews': 'Онлайн-интервью и фокус-группы',
      'website-app-testing': 'Тест сайтов и приложений',
      'store-visits': 'Визиты в магазины и офисы',
      'product-testing': 'Тестирование продуктов и услуг',
      'other': 'Другое',
      // Старые категории для обратной совместимости
      general: 'Онлайн-опросы',
      market: 'Онлайн-опросы',
      'market-research': 'Онлайн-опросы',
    };
    return map[key] || key;
  };

  const translateStatus = (status) => {
    const map = {
      active: 'Активен',
      draft: 'Черновик',
      pending: 'На модерации',
      pending_moderation: 'На модерации',
      rejected: 'Отклонён',
      completed: 'Завершён',
    };
    return map[status] || status;
  };

  useEffect(() => {
    fetchSurveyDetails();
    fetchCurrentUser();
    fetchProfileSurveyQuestions();
  }, [id]);

  const fetchSurveyDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/surveys/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSurvey(res.data);
    } catch (err) {
      console.error('Error fetching survey details:', err);
      
      // Check if profile is incomplete
      if (err.response?.data?.error === 'incomplete_profile') {
        const message = err.response.data.message;
        if (window.confirm(`${message}\n\nПерейти к заполнению профиля?`)) {
          navigate('/respondent/profile');
        } else {
          navigate('/respondent/dashboard');
        }
        return;
      }
      
      setError('Failed to load survey details');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API_BASE.replace('/api','')}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMe(res.data);
    } catch (err) {
      console.warn('Failed to fetch current user for dynamicProfile:', err?.response?.data || err.message);
    }
  };

  const fetchProfileSurveyQuestions = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API_BASE}/surveys?profileSurveysOnly=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = Array.isArray(res.data?.surveys) ? res.data.surveys : res.data; // support both shapes
      const map = {};
      (list || []).forEach(s => {
        (s.questions || []).forEach(q => { if (q && q.id) map[q.id] = q.title || q.text || 'Профильный вопрос'; });
      });
      setProfileQuestionTitles(map);
    } catch (err) {
      console.warn('Failed to fetch profile surveys for question titles:', err?.response?.data || err.message);
    }
  };

  // Compute only profile-based dynamic conditions that match current user
  const buildMatchingProfileConditions = (survey, user) => {
    if (!survey) return [];
    const quotas = Array.isArray(survey.quotas) ? survey.quotas : [];
    if (!user || !user.dynamicProfile) {
      // without user dynamic profile we cannot verify; return aggregated dynamic conditions (deduped)
      const setByQuestion = new Map();
      for (const q of quotas) {
        const dyn = q?.criteria?.dynamic;
        if (!Array.isArray(dyn)) continue;
        for (const cond of dyn) {
          if (!cond?.questionId || !Array.isArray(cond.answers)) continue;
          if (!setByQuestion.has(cond.questionId)) setByQuestion.set(cond.questionId, new Set());
          cond.answers.forEach(a => setByQuestion.get(cond.questionId).add(a));
        }
      }
      return Array.from(setByQuestion.entries()).map(([qid, ansSet]) => ({ questionId: qid, answers: Array.from(ansSet) }));
    }
    const matched = new Map(); // questionId -> Set(answers)
    const profile = user.dynamicProfile || {};
    for (const q of quotas) {
      const dyn = q?.criteria?.dynamic;
      if (!Array.isArray(dyn)) continue;
      for (const cond of dyn) {
        const qid = cond?.questionId;
        const expected = Array.isArray(cond?.answers) ? cond.answers.filter(Boolean) : [];
        if (!qid || expected.length === 0) continue;
        const actual = profile[qid];
        let matchedHere = false;
        if (Array.isArray(actual)) {
          matchedHere = actual.some(a => expected.includes(a));
        } else {
          matchedHere = expected.includes(actual);
        }
        if (matchedHere) {
          if (!matched.has(qid)) matched.set(qid, new Set());
          expected.forEach(a => { if ((Array.isArray(actual) && actual.includes(a)) || actual === a) matched.get(qid).add(a); });
        }
      }
    }
    return Array.from(matched.entries()).map(([questionId, set]) => ({ questionId, answers: Array.from(set) }));
  };

  // Build human-readable audience summary from quotas (including dynamic profile conditions)
  const buildAudienceFromQuotas = (survey) => {
    const quotas = Array.isArray(survey.quotas) ? survey.quotas : [];
    if (quotas.length === 0) return null;

    const agg = {
      gender: new Set(),
      education: new Set(),
      maritalStatus: new Set(),
      employment: new Set(),
      regions: new Set(),
      cities: new Set(),
      regionScopes: new Set(),
      ageMin: Number.POSITIVE_INFINITY,
      ageMax: Number.NEGATIVE_INFINITY,
      dynamics: new Map() // questionId -> Set(answers)
    };

    for (const q of quotas) {
      const c = q.criteria || {};
      // Accept both array and single-value forms
      const addFlexible = (value, targetSet) => {
        if (!value) return;
        if (Array.isArray(value)) value.filter(Boolean).forEach(v => targetSet.add(v));
        else if (typeof value === 'string') targetSet.add(value);
      };
      addFlexible(c.gender, agg.gender);
      addFlexible(c.education, agg.education);
      addFlexible(c.maritalStatus, agg.maritalStatus);
      addFlexible(c.employment, agg.employment);
      addFlexible(c.regions, agg.regions);
      addFlexible(c.region, agg.regions);
      addFlexible(c.cities, agg.cities);
      addFlexible(c.city, agg.cities);
      addFlexible(c.regionScope, agg.regionScopes);

      if (c.ageRange && typeof c.ageRange.min === 'number' && typeof c.ageRange.max === 'number') {
        agg.ageMin = Math.min(agg.ageMin, c.ageRange.min);
        agg.ageMax = Math.max(agg.ageMax, c.ageRange.max);
      }

      if (Array.isArray(c.dynamic)) {
        for (const cond of c.dynamic) {
          if (!cond || !cond.questionId || !Array.isArray(cond.answers)) continue;
          if (!agg.dynamics.has(cond.questionId)) agg.dynamics.set(cond.questionId, new Set());
          const set = agg.dynamics.get(cond.questionId);
          cond.answers.filter(Boolean).forEach(a => set.add(a));
        }
      }
    }

    // Convert dynamics to readable items using survey questions
    const dynamicItems = [];
    if (agg.dynamics.size > 0 && Array.isArray(survey.questions)) {
      for (const [questionId, answersSet] of agg.dynamics.entries()) {
        const qObj = survey.questions.find(q => q.id === questionId);
        if (!qObj) continue;
        const title = qObj.title || qObj.text || 'Вопрос';
        const answers = Array.from(answersSet);
        dynamicItems.push({ title, answers });
      }
    }

    const asArray = (s) => Array.from(s);
    const ageValid = Number.isFinite(agg.ageMin) && Number.isFinite(agg.ageMax) && agg.ageMin <= agg.ageMax;

    return {
      gender: asArray(agg.gender),
      education: asArray(agg.education),
      maritalStatus: asArray(agg.maritalStatus),
      employment: asArray(agg.employment),
      regions: asArray(agg.regions),
      cities: asArray(agg.cities),
      regionScopes: asArray(agg.regionScopes),
      ageRange: ageValid ? { min: agg.ageMin, max: agg.ageMax } : null,
      dynamics: dynamicItems
    };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !survey) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Опрос не найден'}</Alert>
        <Button onClick={() => navigate('/respondent/dashboard')} sx={{ mt: 2 }}>
          Вернуться к списку
        </Button>
      </Box>
    );
  }

  const handleParticipate = async () => {
    try {
      const token = localStorage.getItem('token');

      // External survey: call external-complete endpoint and redirect
      if (survey.isExternalSurvey && survey.externalUrl) {
        setExternalLoading(true);
        try {
          const result = await axios.post(`${API_BASE}/surveys/${id}/external-complete`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          // Open external URL in new tab and navigate back
          window.open(result.data.externalUrl, '_blank');
          navigate('/respondent/dashboard');
        } catch (err) {
          if (err.response?.data?.error === 'incomplete_profile') {
            const message = err.response.data.message;
            if (window.confirm(`${message}\n\nПерейти к заполнению профиля?`)) {
              navigate('/respondent/profile');
            }
          } else {
            alert(err.response?.data?.error || 'Не удалось начать опрос');
          }
        } finally {
          setExternalLoading(false);
        }
        return;
      }

      // Check profile completeness before navigating
      await axios.get(`${API_BASE}/surveys/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate(`/respondent/surveys/${id}/take`);
    } catch (err) {
      if (err.response?.data?.error === 'incomplete_profile') {
        const message = err.response.data.message;
        if (window.confirm(`${message}\n\nПерейти к заполнению профиля?`)) {
          navigate('/respondent/profile');
        }
      } else {
        alert('Не удалось начать опрос');
      }
    }
  };

  return (
    <Box>
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 1.5, md: 4 },
          mx: { xs: 0, md: 3 },
          mb: 3,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: { xs: '8px', md: '32px' },
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography 
          variant="h5" 
          gutterBottom
          sx={{ 
            fontWeight: 700,
            color: '#2d3748',
            mb: 2
          }}
        >
          {survey.title}
        </Typography>
        
        <Box sx={{ my: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {survey.category && (
            <Chip 
              label={translateCategory(survey.category)} 
              sx={{ 
                background: 'linear-gradient(135deg, rgba(116, 185, 255, 0.2), rgba(9, 132, 227, 0.2))',
                color: '#0984e3',
                fontWeight: 600,
                border: '1px solid rgba(9, 132, 227, 0.3)',
                borderRadius: '12px'
              }} 
            />
          )}
          <Chip 
            label={translateStatus(survey.status)} 
            sx={{
              background: survey.status === 'active' 
                ? 'linear-gradient(135deg, rgba(86, 171, 47, 0.2), rgba(45, 122, 31, 0.2))'
                : 'linear-gradient(135deg, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.05))',
              color: survey.status === 'active' ? '#56ab2f' : '#718096',
              fontWeight: 600,
              border: survey.status === 'active' 
                ? '1px solid rgba(86, 171, 47, 0.3)'
                : '1px solid rgba(0, 0, 0, 0.1)',
              borderRadius: '12px'
            }}
          />
        </Box>

        <Typography 
          variant="body1" 
          paragraph
          sx={{ 
            color: '#718096',
            lineHeight: 1.7,
            fontWeight: 500
          }}
        >
          {survey.description}
        </Typography>

        <Divider sx={{ my: 3, opacity: 0.3 }} />

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {(() => {
            const r = survey.reward || {};
            const money = typeof r.moneyAmount === 'number' ? r.moneyAmount
                          : (r.type === 'money' ? (r.amount || 0) : (r.money?.amount || 0));
            const points = typeof r.pointsAmount === 'number' ? r.pointsAmount
                           : (r.type === 'points' ? (r.amount || 0) : (r.points?.amount || 0));
            return (
              <>
                {money > 0 && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: { xs: '16px', md: '20px' },
                        background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                        boxShadow: '0 4px 20px rgba(9, 132, 227, 0.3)',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Typography 
                        variant="subtitle2" 
                        gutterBottom
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.9)',
                          fontWeight: 600,
                          mb: 1.5
                        }}
                      >
                        Награда (деньги)
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUpIcon sx={{ color: 'white', fontSize: 28 }} />
                        <Typography 
                          variant="h5"
                          sx={{ 
                            color: 'white',
                            fontWeight: 700
                          }}
                        >
                          {Number(money).toFixed(1)} BYN
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
                {points > 0 && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: { xs: '16px', md: '20px' },
                        background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)',
                        boxShadow: '0 4px 20px rgba(253, 203, 110, 0.3)',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Typography 
                        variant="subtitle2" 
                        gutterBottom
                        sx={{ 
                          color: 'rgba(0, 0, 0, 0.7)',
                          fontWeight: 600,
                          mb: 1.5
                        }}
                      >
                        Подарочные баллы
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StarsIcon sx={{ color: '#2d3748', fontSize: 28 }} />
                        <Typography 
                          variant="h5"
                          sx={{ 
                            color: '#2d3748',
                            fontWeight: 700
                          }}
                        >
                          {Number(points).toFixed(1)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </>
            );
          })()}

          <Grid item xs={12} sm={6} md={3}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: { xs: '16px', md: '20px' },
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Typography 
                variant="subtitle2" 
                gutterBottom
                sx={{ 
                  color: '#718096',
                  fontWeight: 600,
                  mb: 1.5
                }}
              >
                Время прохождения
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimeIcon sx={{ color: '#0984e3', fontSize: 28 }} />
                <Typography 
                  variant="h5"
                  sx={{ 
                    color: '#2d3748',
                    fontWeight: 700
                  }}
                >
                  {survey.estimatedTime || 'Н/Д'} мин
                </Typography>
              </Box>
            </Box>
          </Grid>

          {!survey.isExternalSurvey && (
          <Grid item xs={12} sm={6} md={3}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: { xs: '16px', md: '20px' },
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Typography 
                variant="subtitle2" 
                gutterBottom
                sx={{ 
                  color: '#718096',
                  fontWeight: 600,
                  mb: 1.5
                }}
              >
                Вопросов
              </Typography>
              <Typography 
                variant="h5"
                sx={{ 
                  color: '#2d3748',
                  fontWeight: 700
                }}
              >
                {survey.questions?.length || 0}
              </Typography>
            </Box>
          </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 3, opacity: 0.3 }} />

        {(() => {
          const quotas = Array.isArray(survey.quotas) ? survey.quotas : [];
          const audience = buildAudienceFromQuotas(survey);
          // We now show only the specific profile-based reasons why this survey is visible to the current user
          // Logic: find first quota whose criteria match user (approx using fields present in survey.userContext if supplied later)
          // For now we aggregate dynamic conditions (answers) and basic demographic constraints without showing counts.
          if (audience) {
            const matched = buildMatchingProfileConditions(survey, me);
            return (
              <>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 700,
                    color: '#2d3748',
                    mb: 2
                  }}
                >
                  Условия
                </Typography>
                <List sx={{ 
                  background: 'rgba(255, 255, 255, 0.5)',
                  borderRadius: { xs: '16px', md: '20px' },
                  p: 1
                }}>
                  {matched.length === 0 && (
                    <ListItem
                      sx={{
                        borderRadius: '12px',
                        mb: 0.5,
                        '&:hover': {
                          background: 'rgba(116, 185, 255, 0.05)'
                        }
                      }}
                    >
                      <ListItemIcon>
                        <RequirementIcon sx={{ color: '#0984e3' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Профильные условия" 
                        secondary="Соответствуют данным вашего профиля"
                        primaryTypographyProps={{
                          fontWeight: 600,
                          color: '#2d3748'
                        }}
                        secondaryTypographyProps={{
                          color: '#718096'
                        }}
                      />
                    </ListItem>
                  )}
                  {matched.map((m, idx) => {
                    const title = profileQuestionTitles[m.questionId] || 'Профильное условие';
                    return (
                      <ListItem 
                        key={`dyn_${idx}`}
                        sx={{
                          borderRadius: '12px',
                          mb: 0.5,
                          '&:hover': {
                            background: 'rgba(116, 185, 255, 0.05)'
                          }
                        }}
                      >
                        <ListItemIcon>
                          <RequirementIcon sx={{ color: '#0984e3' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={title} 
                          secondary={m.answers.join(', ')}
                          primaryTypographyProps={{
                            fontWeight: 600,
                            color: '#2d3748'
                          }}
                          secondaryTypographyProps={{
                            color: '#718096'
                          }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </>
            );
          }

          // Fallback to explicit targetAudience if provided and no quotas
          if (survey.targetAudience && Object.keys(survey.targetAudience).length > 0) {
            return (
              <>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 700,
                    color: '#2d3748',
                    mb: 2
                  }}
                >
                  Целевая аудитория
                </Typography>
                <List sx={{ 
                  background: 'rgba(255, 255, 255, 0.5)',
                  borderRadius: { xs: '16px', md: '20px' },
                  p: 1
                }}>
                  {survey.targetAudience.gender && survey.targetAudience.gender.length > 0 && (
                    <ListItem
                      sx={{
                        borderRadius: '12px',
                        mb: 0.5,
                        '&:hover': {
                          background: 'rgba(116, 185, 255, 0.05)'
                        }
                      }}
                    >
                      <ListItemIcon>
                        <RequirementIcon sx={{ color: '#0984e3' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Пол" 
                        secondary={survey.targetAudience.gender.join(', ')}
                        primaryTypographyProps={{
                          fontWeight: 600,
                          color: '#2d3748'
                        }}
                        secondaryTypographyProps={{
                          color: '#718096'
                        }}
                      />
                    </ListItem>
                  )}
                  {survey.targetAudience.ageRange && (
                    <ListItem
                      sx={{
                        borderRadius: '12px',
                        mb: 0.5,
                        '&:hover': {
                          background: 'rgba(116, 185, 255, 0.05)'
                        }
                      }}
                    >
                      <ListItemIcon>
                        <RequirementIcon sx={{ color: '#0984e3' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Возраст" 
                        secondary={`${survey.targetAudience.ageRange.min} - ${survey.targetAudience.ageRange.max} лет`}
                        primaryTypographyProps={{
                          fontWeight: 600,
                          color: '#2d3748'
                        }}
                        secondaryTypographyProps={{
                          color: '#718096'
                        }}
                      />
                    </ListItem>
                  )}
                  {survey.targetAudience.regions && survey.targetAudience.regions.length > 0 && (
                    <ListItem
                      sx={{
                        borderRadius: '12px',
                        mb: 0.5,
                        '&:hover': {
                          background: 'rgba(116, 185, 255, 0.05)'
                        }
                      }}
                    >
                      <ListItemIcon>
                        <RequirementIcon sx={{ color: '#0984e3' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Регионы" 
                        secondary={survey.targetAudience.regions.join(', ')}
                        primaryTypographyProps={{
                          fontWeight: 600,
                          color: '#2d3748'
                        }}
                        secondaryTypographyProps={{
                          color: '#718096'
                        }}
                      />
                    </ListItem>
                  )}
                  {survey.targetAudience.cities && survey.targetAudience.cities.length > 0 && (
                    <ListItem
                      sx={{
                        borderRadius: '12px',
                        mb: 0.5,
                        '&:hover': {
                          background: 'rgba(116, 185, 255, 0.05)'
                        }
                      }}
                    >
                      <ListItemIcon>
                        <RequirementIcon sx={{ color: '#0984e3' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Города" 
                        secondary={survey.targetAudience.cities.join(', ')}
                        primaryTypographyProps={{
                          fontWeight: 600,
                          color: '#2d3748'
                        }}
                        secondaryTypographyProps={{
                          color: '#718096'
                        }}
                      />
                    </ListItem>
                  )}
                  {survey.targetAudience.education && survey.targetAudience.education.length > 0 && (
                    <ListItem
                      sx={{
                        borderRadius: '12px',
                        mb: 0.5,
                        '&:hover': {
                          background: 'rgba(116, 185, 255, 0.05)'
                        }
                      }}
                    >
                      <ListItemIcon>
                        <RequirementIcon sx={{ color: '#0984e3' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Образование" 
                        secondary={survey.targetAudience.education.join(', ')}
                        primaryTypographyProps={{
                          fontWeight: 600,
                          color: '#2d3748'
                        }}
                        secondaryTypographyProps={{
                          color: '#718096'
                        }}
                      />
                    </ListItem>
                  )}
                </List>
              </>
            );
          }
          return null;
        })()}

        {!survey.isExternalSurvey && (
        <Alert 
          severity="info" 
          icon={<WarningIcon />}
          sx={{ 
            mt: 3,
            borderRadius: { xs: '12px', md: '16px' },
            background: 'linear-gradient(135deg, rgba(116, 185, 255, 0.1), rgba(9, 132, 227, 0.1))',
            border: '1px solid rgba(9, 132, 227, 0.2)',
            '& .MuiAlert-icon': {
              color: '#0984e3'
            },
            '& .MuiAlert-message': {
              color: '#2d3748',
              fontWeight: 600
            }
          }}
        >
          Пожалуйста, отвечайте честно на все вопросы. Ваши ответы важны для этого исследования.
        </Alert>
        )}

        {survey.isExternalSurvey && (
          <Alert 
            severity="warning" 
            sx={{ 
              mt: 3,
              borderRadius: { xs: '12px', md: '16px' },
              background: 'linear-gradient(135deg, rgba(253, 203, 110, 0.2), rgba(225, 177, 44, 0.2))',
              border: '1px solid rgba(225, 177, 44, 0.3)',
            }}
          >
            Это внешний опрос. После нажатия кнопки вы будете перенаправлены на сторонний сайт, а награда будет начислена автоматически.
          </Alert>
        )}

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleParticipate}
            disabled={externalLoading}
            sx={{
              background: survey.isExternalSurvey 
                ? 'linear-gradient(135deg, #fdcb6e 0%, #e17055 100%)'
                : 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
              color: 'white',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '16px',
              px: 6,
              py: 1.5,
              fontSize: '1.1rem',
              boxShadow: survey.isExternalSurvey
                ? '0 4px 20px rgba(225, 112, 85, 0.4)'
                : '0 4px 20px rgba(9, 132, 227, 0.4)',
              '&:hover': {
                background: survey.isExternalSurvey
                  ? 'linear-gradient(135deg, #e17055 0%, #fdcb6e 100%)'
                  : 'linear-gradient(135deg, #0984e3 0%, #74b9ff 100%)',
                boxShadow: '0 6px 25px rgba(9, 132, 227, 0.5)'
              }
            }}
          >
            {externalLoading ? 'Переход...' : (survey.isExternalSurvey ? 'Перейти к опросу' : 'Пройти опрос')}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}